import * as React from 'react';

import { Recipient } from 'api/api';

import { Spinner } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { AriaGrid } from '~src/ui/aria-grid';
import { AnimatedButton } from '~src/ui/button';
import { useListState } from '~src/ui/list-state';

import { AddButtonUI } from '~address-form/components/add-button';
import { prepareNewAddress } from '~address-form/utils/prepare-address';

import { RecipientFSMPayload } from '../state-machines/shared-types';
import { AddressListItem } from './address-list-item';

interface AddressListFSMProps {
  /**
   * component is used as part of main checkout widget as well as fullscreen modal
   * so it could not have access to RecipientFSMPayload (mirror of state machine), and rather
   * original reference. For now pass the recipientState as prop.
   *
   * FIXME: maybe add useFSMContext Provider stub for main checkout widget
   */
  recipientState: RecipientFSMPayload;
  handleSubmit: (address: Recipient) => void;
  handleAddNewAddress: (address: Recipient) => void;
  handleOpenAddress: (address: Recipient) => void;
  buttonMessage?: string;
}

export const AddressListFSM: React.FC<AddressListFSMProps> = ({
  recipientState,
  buttonMessage,
  handleSubmit,
  handleAddNewAddress,
  handleOpenAddress,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();

  const getAddresses = () => {
    if (globalState.address_book) {
      return globalState.address_book.addresses!;
    } else {
      return [globalState.recipient!];
    }
  };

  const buttonText = buttonMessage || tICU('widget.delivery_address.SAVE_CHOICE_BTN');

  const defaultSelectedItem = globalState.recipient?.id
    ? globalState.recipient.id
    : globalState.address_book?.addresses?.[0].id;

  const listState = useListState({
    items: getAddresses(),
    getKey: (address: Recipient) => address.id || '',
    defaultSelectedKey: defaultSelectedItem || '',
  });

  const saveAddress = () => {
    const currentItem = listState.getSelectedItem();
    if (currentItem?.is_dirty) {
      handleOpenAddress(currentItem);
    } else if (currentItem) {
      handleSubmit(currentItem);
    }
  };

  const newAddress = prepareNewAddress(
    globalState.address_form_configuration?.features,
    globalState.prefill_recipient
  );

  const listElement = (
    <AriaGrid listState={listState}>
      {(address: Recipient) => {
        return (
          <AddressListItem
            key={address.id}
            address={address}
            listState={listState}
            handleClick={() => handleOpenAddress(address)}
          />
        );
      }}
    </AriaGrid>
  );

  return (
    <>
      {listElement}
      <AddButtonUI
        label={tICU('widget.delivery_address.ADD_DIFFERENT_ADDRESS')}
        onClick={() => handleAddNewAddress(newAddress)}
      />
      <AnimatedButton onClick={saveAddress} animKey={recipientState.meta.fetching.toString()}>
        {recipientState.meta.fetching ? <Spinner size="small" /> : buttonText}
      </AnimatedButton>
    </>
  );
};
